(() => {
  const ROOT_SELECTOR = '[data-judgeme-lazy-root]';
  const scheduled = new WeakSet();
  const styleRequests = new Map();
  const scriptRequests = new Map();

  const loadStylesheet = (url) => {
    if (!url) return Promise.resolve();
    if (styleRequests.has(url)) return styleRequests.get(url);

    const existing = document.querySelector(`link[rel="stylesheet"][href="${CSS.escape(url)}"]`);
    if (existing) return Promise.resolve();

    const request = new Promise((resolve, reject) => {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = url;
      link.addEventListener('load', resolve, { once: true });
      link.addEventListener('error', reject, { once: true });
      document.head.appendChild(link);
    });

    styleRequests.set(url, request);
    return request;
  };

  const loadScript = (url) => {
    if (!url) return Promise.resolve();
    if (scriptRequests.has(url)) return scriptRequests.get(url);

    const existing = document.querySelector(`script[src="${CSS.escape(url)}"]`);
    if (existing) return Promise.resolve();

    const request = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = url;
      script.async = true;
      script.addEventListener('load', resolve, { once: true });
      script.addEventListener('error', reject, { once: true });
      document.head.appendChild(script);
    });

    scriptRequests.set(url, request);
    return request;
  };

  const activate = async (root) => {
    if (!root || root.dataset.judgemeLazyState === 'loading' || root.dataset.judgemeLazyState === 'loaded') return;

    root.dataset.judgemeLazyState = 'loading';
    try {
      await Promise.all([
        loadStylesheet(root.dataset.judgemeStyleSrc),
        loadScript(root.dataset.judgemeScriptSrc),
      ]);
      root.dataset.judgemeLazyState = 'loaded';
      root.dispatchEvent(new CustomEvent('perfumiarz:judgeme-ready', { bubbles: true }));
    } catch (error) {
      root.dataset.judgemeLazyState = 'error';
      console.error('[Perfumiarz reviews] Unable to load reviews.', error);
    }
  };

  const observer = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        entries.forEach(({ target, isIntersecting }) => {
          if (!isIntersecting) return;
          observer.unobserve(target);
          activate(target);
        });
      }, { rootMargin: '900px 0px' })
    : null;

  const schedule = (root) => {
    if (!root || scheduled.has(root)) return;
    scheduled.add(root);

    const activateFromInteraction = () => {
      observer?.unobserve(root);
      activate(root);
    };

    root.addEventListener('pointerdown', activateFromInteraction, { once: true, passive: true });
    root.addEventListener('focusin', activateFromInteraction, { once: true });

    if (window.Shopify?.designMode || !observer) {
      activate(root);
      return;
    }

    observer.observe(root);
  };

  const initialize = (scope = document) => {
    if (scope.matches?.(ROOT_SELECTOR)) schedule(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach(schedule);
  };

  initialize();
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll?.(ROOT_SELECTOR).forEach((root) => observer?.unobserve(root));
  });
})();
